import * as THREE from 'three';
import { UIState, ToolType, AppMode } from './store';
import { AssetManager } from '../engine/Assets';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

// --- Purchasing Preferences Logic ---
const STORAGE_KEY_PREFS = 'logsim3d_preferred_stores';

interface StorePrefs {
  defaults: Record<string, boolean>;
  customs: string[]; // List of domains
}

const DEFAULT_STORES_LIST = [
  { id: 'amazon', name: 'Amazon', domain: 'amazon.com' },
  { id: 'walmart', name: 'Walmart', domain: 'walmart.com' },
  { id: 'stihl', name: 'Stihl', domain: 'stihlusa.com' },
  { id: 'husqvarna', name: 'Husqvarna', domain: 'husqvarna.com' },
  { id: 'homedepot', name: 'Home Depot', domain: 'homedepot.com' },
  { id: 'acmetools', name: 'Acme Tools', domain: 'acmetools.com' }
];

function getPurchasingConfig(): StorePrefs {
  const stored = localStorage.getItem(STORAGE_KEY_PREFS);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.warn('Failed to parse store prefs, using defaults', e);
    }
  }
  // First time experience: default all to true
  const defaults: Record<string, boolean> = {};
  DEFAULT_STORES_LIST.forEach(s => defaults[s.id] = true);
  return { defaults, customs: [] };
}

function savePurchasingConfig(prefs: StorePrefs) {
  localStorage.setItem(STORAGE_KEY_PREFS, JSON.stringify(prefs));
}

export interface PPEModelConfig {
  modelPath: string;
  manScale?: [number, number, number];
  showcaseScale?: [number, number, number];
  manPosition?: [number, number, number];
  manRotation?: [number, number, number];
}

export interface PPEItem {
  id: string;
  name: string;
  description: string;
  functions: string;
  priceRange: string;
  buyUrl: string;
  models?: PPEModelConfig[];
  category?: 'ppe' | 'emergency';
}

export const PPE_ITEMS: PPEItem[] = [
  {
    id: 'hardhat',
    name: 'Hard Hat / Helmet',
    description: 'Protects against falling debris, overhead hazards (widowmakers), and impact.',
    functions: 'Absorbs shock from falling objects, provides penetration resistance, and offers lateral protection.',
    priceRange: '$35.00 - $95.00',
    buyUrl: 'https://example.com/buy-hardhat',
    models: [
      {
        modelPath: '/assets/models/forestry_hardhat.glb',
        manScale: [0.12, 0.12, 0.12],
        showcaseScale: [1.0, 1.0, 1.0],
        manPosition: [0, 1.70, 0.05],
        manRotation: [0, 0, 0]
      }
    ]
  },
  {
    id: 'eyeprotection',
    name: 'Eye Protection',
    description: 'Mesh visor or safety glasses designed to shield eyes from wood chips and dust.',
    functions: 'Prevents high-velocity sawdust, wood chips, and small branches from causing ocular injuries.',
    priceRange: '$15.00 - $40.00',
    buyUrl: 'https://example.com/buy-eye-protection',
    models: [
      {
        modelPath: '/assets/models/safety_goggles.glb',
        manScale: [0.75, 0.75, 0.75],
        showcaseScale: [7, 7, 7],
        manPosition: [0, 1.66, 0.105],
        manRotation: [0, 0, 0]
      }
    ]
  },
  {
    id: 'earprotection',
    name: 'Ear Protection',
    description: 'High-decibel earmuffs or earplugs designed to prevent noise-induced hearing loss.',
    functions: 'Reduces chainsaw noise exposure (typically 110+ dB) down to safe operational decibel levels.',
    priceRange: '$25.00 - $65.00',
    buyUrl: 'https://example.com/buy-ear-protection'
  },
  {
    id: 'gloves',
    name: 'Heavy-Duty Gloves',
    description: 'Protective leather or synthetic gloves for grip, abrasion protection, and vibration damping.',
    functions: 'Improves chainsaw grip, reduces hand fatigue from vibration, and protects skin from splinters.',
    priceRange: '$20.00 - $50.00',
    buyUrl: 'https://example.com/buy-gloves',
    models: [
      {
        modelPath: '/assets/models/right_glove.glb',
        manScale: [1.06, 1.06, 1.06],
        showcaseScale: [7, 7, 7],
        manPosition: [-0.429, 0.845, 0.114],
        manRotation: [0.0, 0, 0]
      },
      {
        modelPath: '/assets/models/left_glove.glb',
        manScale: [1.06, 1.06, 1.06],
        showcaseScale: [0, 0, 0],
        manPosition: [0.429, 0.849, 0.112],
        manRotation: [0.0, 0, 0]
      }
    ]
  },
  {
    id: 'chaps',
    name: 'Kevlar Chainsaw Chaps',
    description: 'Safety chaps filled with layers of Kevlar or polyester fibers that clog and stop a moving chain.',
    functions: 'Instantly jams the chainsaw drive sprocket upon contact, preventing severe leg lacerations.',
    priceRange: '$75.00 - $180.00',
    buyUrl: 'https://example.com/buy-chaps',
    models: [
      {
        modelPath: '/assets/models/chaps.glb',
        manScale: [1.06, 1.06, 1.06],
        showcaseScale: [2, 2, 2],
        manPosition: [0.0, 0.0, 0.009],
        manRotation: [0.0, 0, 0]
      }]
  },
  {
    id: 'boots',
    name: 'Steel-Toed Boots',
    description: 'Rugged leather logging boots with steel/composite toes and deep lugs for traction.',
    functions: 'Protects toes from crush hazards (heavy logs) and provides stability/grip on uneven, slippery terrain.',
    priceRange: '$120.00 - $300.00',
    buyUrl: 'https://example.com/buy-boots',
    models: [
      {
        modelPath: '/assets/models/right_boot.glb',
        manScale: [1.41, 1.41, 1.41],
        showcaseScale: [7, 7, 7],
        manPosition: [-0.467, 0., -0.107],
        manRotation: [0.001, -0.474, -0.009]
      },
      {
        modelPath: '/assets/models/left_boot.glb',
        manScale: [1.41, 1.41, 1.41],
        showcaseScale: [7, 7, 7],
        manPosition: [-0.295, -0.072, 0.189],
        manRotation: [0.042, 0.335, 0.107]
      }
    ]
  },
  {
    id: 'firstaid',
    name: 'First Aid Kit',
    category: 'emergency',
    description: 'Emergency trauma first aid kit equipped with pressure dressings, tourniquet, antiseptic, and bandages.',
    functions: 'Provides rapid response tools critical for bleeding control and wound treatment in chainsaw lacerations or field injuries.',
    priceRange: '$35.00 - $95.00',
    buyUrl: 'https://example.com/buy-first-aid-kit'
  }
];

class PPEPreview {
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private currentMesh: THREE.Group | null = null;
  private animationId: number = 0;
  private isRunning: boolean = false;

  constructor() {
    const canvas = document.getElementById('ppe-preview-canvas') as HTMLCanvasElement;
    if (!canvas) return;

    const width = canvas.clientWidth || 300;
    const height = canvas.clientHeight || 160;

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    this.camera.position.set(0, 0, 3);

    this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    this.renderer.setSize(width, height, false);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    pmremGenerator.compileEquirectangularShader();
    const roomEnv = new RoomEnvironment();
    this.scene.environment = pmremGenerator.fromScene(roomEnv).texture;
    pmremGenerator.dispose();
    roomEnv.dispose();

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    this.scene.add(ambientLight);

    const dirLight1 = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight1.position.set(5, 5, 5);
    this.scene.add(dirLight1);

    const dirLight2 = new THREE.DirectionalLight(0x06b6d4, 0.6);
    dirLight2.position.set(-5, 3, 2);
    this.scene.add(dirLight2);

    const resizeObserver = new ResizeObserver(() => {
      if (!canvas || !this.renderer || !this.camera) return;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(w, h, false);
    });
    resizeObserver.observe(canvas);
  }

  public setItem(models?: PPEModelConfig[]) {
    if (!this.scene) return;

    if (this.currentMesh) {
      this.scene.remove(this.currentMesh);
      this.currentMesh = null;
    }

    if (!models || models.length === 0) return;

    const group = new THREE.Group();

    models.forEach((modelConfig) => {
      const model = AssetManager.getModel(modelConfig.modelPath);
      if (model) {
        const clonedScene = model.scene.clone();

        clonedScene.traverse((child) => {
          if ((child as THREE.Mesh).isMesh) {
            const mesh = child as THREE.Mesh;
            if (mesh.material) {
              const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
              for (const mat of materials) {
                if (mat instanceof THREE.MeshPhysicalMaterial && mat.transmission > 0) {
                  mat.transmission = 0;
                  mat.transparent = true;
                  mat.opacity = 0.4;
                  mat.roughness = Math.min(mat.roughness, 0.2);
                  mat.needsUpdate = true;
                }
              }
            }
          }
        });

        if (modelConfig.showcaseScale) {
          clonedScene.scale.set(
            modelConfig.showcaseScale[0],
            modelConfig.showcaseScale[1],
            modelConfig.showcaseScale[2]
          );
        }

        group.add(clonedScene);
      }
    });

    if (group.children.length > 0) {
      const box = new THREE.Box3().setFromObject(group);
      const center = new THREE.Vector3();
      box.getCenter(center);
      group.children.forEach((child) => {
        child.position.sub(center);
      });

      this.currentMesh = group;
      this.scene.add(this.currentMesh);
    }
  }

  public start() {
    if (this.isRunning) return;
    this.isRunning = true;
    this.animate();
  }

  public stop() {
    this.isRunning = false;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = 0;
    }
  }

  private animate() {
    if (!this.isRunning) return;

    this.animationId = requestAnimationFrame(() => this.animate());

    if (this.currentMesh) {
      this.currentMesh.rotation.y += 0.01;
    }

    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }
}

export function initHUD() {
  const throttleFill = document.getElementById('throttle-fill');
  const throttleToggle = document.getElementById('throttle-toggle') as HTMLInputElement;

  if (throttleToggle) {
    throttleToggle.addEventListener('click', (e) => {
      e.preventDefault();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      window.dispatchEvent(new KeyboardEvent('keydown', { key: 't' }));
    });
  }

  const debugPanel = document.getElementById('debug-panel');

  // Modals
  const modalHelp = document.getElementById('modal-help');
  const btnHelp = document.getElementById('btn-help');

  const modalStuck = document.getElementById('modal-stuck');
  const btnDismissStuck = document.getElementById('btn-dismiss-stuck');

  // Modes
  const modePPE = document.getElementById('mode-ppe') as HTMLInputElement;
  const modeOHLEC = document.getElementById('mode-ohlec') as HTMLInputElement;
  const modeSimulator = document.getElementById('mode-simulator') as HTMLInputElement;
  const modeAssessment = document.getElementById('mode-assessment') as HTMLInputElement;

  const btnNextOhlec = document.getElementById('btn-next-ohlec');
  const btnBackPpe = document.getElementById('btn-back-ppe');
  const btnStartSimulator = document.getElementById('btn-start-simulator');

  const ppePanel = document.getElementById('ppe-panel');
  const ohlecPanel = document.getElementById('ohlec-panel');
  const assessmentPanel = document.getElementById('assessment-panel');
  const modeBackdrop = document.getElementById('mode-backdrop');
  const simulatorControls = document.querySelectorAll('.simulator-control');

  const setMode = (mode: AppMode) => {
    UIState.update({ activeMode: mode });
  };

  modePPE?.addEventListener('change', () => setMode('PPE'));
  modeOHLEC?.addEventListener('change', () => {
    window.location.href = '/ohlec.html';
  });
  modeSimulator?.addEventListener('change', () => setMode('SIMULATOR'));
  modeAssessment?.addEventListener('change', () => setMode('ASSESSMENT'));

  btnNextOhlec?.addEventListener('click', () => {
    window.location.href = '/ohlec.html';
  });
  btnBackPpe?.addEventListener('click', () => setMode('PPE'));
  btnStartSimulator?.addEventListener('click', () => setMode('SIMULATOR'));

  // Exam Logic
  const optionCards = document.querySelectorAll('.exam-option-card');
  const btnSubmitExam = document.getElementById('btn-submit-exam');
  const btnResetExam = document.getElementById('btn-reset-exam');
  const examFeedback = document.getElementById('exam-feedback');

  optionCards.forEach(card => {
    card.addEventListener('click', () => {
      optionCards.forEach(c => c.classList.remove('selected'));
      card.classList.add('selected');
      const radio = card.querySelector('input[type="radio"]') as HTMLInputElement;
      if (radio) radio.checked = true;
    });
  });

  btnSubmitExam?.addEventListener('click', () => {
    const selectedRadio = document.querySelector('input[name="examQuestion1"]:checked') as HTMLInputElement;
    if (!selectedRadio || !examFeedback) return;

    examFeedback.classList.remove('hidden', 'correct-bg', 'incorrect-bg');
    optionCards.forEach(c => c.classList.remove('correct', 'incorrect'));

    const parentCard = selectedRadio.closest('.exam-option-card');

    if (selectedRadio.value === 'B') {
      if (parentCard) parentCard.classList.add('correct');
      examFeedback.classList.add('correct-bg');
      examFeedback.innerHTML = '<b>✓ Correct!</b> Chainsaw chaps contain cut-resistant fibers (like Kevlar/Aramid) designed to pull into the saw sprocket and stop the chain instantly upon contact.';
    } else {
      if (parentCard) parentCard.classList.add('incorrect');
      // Highlight correct B card
      const correctCard = document.querySelector('input[value="B"]')?.closest('.exam-option-card');
      if (correctCard) correctCard.classList.add('correct');

      examFeedback.classList.add('incorrect-bg');
      examFeedback.innerHTML = '<b>✗ Incorrect.</b> Option B is correct. The primary purpose of chainsaw chaps is to supply high-friction cut-resistant fibers that tangle and stop the saw chain immediately.';
    }
  });

  btnResetExam?.addEventListener('click', () => {
    const radios = document.querySelectorAll('input[name="examQuestion1"]');
    radios.forEach(r => (r as HTMLInputElement).checked = false);
    optionCards.forEach(c => c.classList.remove('selected', 'correct', 'incorrect'));
    if (examFeedback) {
      examFeedback.classList.add('hidden');
      examFeedback.classList.remove('correct-bg', 'incorrect-bg');
      examFeedback.innerHTML = '';
    }
  });

  // Dynamically initialize PPE list buttons
  const ppeListContainer = document.getElementById('ppe-list');
  const emergencyListContainer = document.getElementById('emergency-list');
  if (ppeListContainer) {
    ppeListContainer.innerHTML = '';
  }
  if (emergencyListContainer) {
    emergencyListContainer.innerHTML = '';
  }

  PPE_ITEMS.forEach(item => {
    const targetContainer = (item.category === 'emergency' && emergencyListContainer)
      ? emergencyListContainer
      : ppeListContainer;

    if (!targetContainer) return;

    const btn = document.createElement('div');
    btn.className = 'ppe-item-btn';
    btn.id = `ppe-item-${item.id}`;

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.id = `ppe-cb-${item.id}`;
    checkbox.addEventListener('click', (e) => {
      e.stopPropagation(); // Avoid triggering parent click selection
    });
    checkbox.addEventListener('change', () => {
      const currentEquipped = { ...UIState.getState().equippedPPE };
      currentEquipped[item.id] = checkbox.checked;
      UIState.update({ equippedPPE: currentEquipped });
    });

    const label = document.createElement('span');
    label.className = 'ppe-item-label';
    label.textContent = item.name;

    btn.appendChild(checkbox);
    btn.appendChild(label);

    btn.addEventListener('click', () => {
      UIState.update({ selectedPPEId: item.id });
    });

    targetContainer.appendChild(btn);
  });

  // Setup right detail panel and 3D previewer
  const ppeDetailPanel = document.getElementById('ppe-detail-panel');
  let ppePreview: PPEPreview | null = null;

  function updatePPEDetails(state: any) {
    if (!ppeDetailPanel) return;

    const selectedItem = PPE_ITEMS.find(item => item.id === state.selectedPPEId);
    if (!selectedItem || state.activeMode !== 'PPE') {
      ppeDetailPanel.classList.add('hidden');
      if (ppePreview) {
        ppePreview.stop();
      }
      return;
    }

    ppeDetailPanel.classList.remove('hidden');

    const titleEl = document.getElementById('ppe-detail-title');
    const descEl = document.getElementById('ppe-detail-desc');
    const funcsEl = document.getElementById('ppe-detail-funcs');
    const priceEl = document.getElementById('ppe-detail-price');
    const buyEl = document.getElementById('ppe-detail-buy') as HTMLButtonElement;

    if (titleEl) titleEl.textContent = selectedItem.name;
    if (descEl) descEl.textContent = selectedItem.description;
    if (funcsEl) funcsEl.textContent = selectedItem.functions;
    if (priceEl) priceEl.textContent = selectedItem.priceRange;
    // buyEl dynamically handles click instead of static href

    if (!ppePreview) {
      ppePreview = new PPEPreview();
    }
    ppePreview.setItem(selectedItem.models);
    ppePreview.start();
  }

  // Tools
  const toolButtons = document.querySelectorAll('.tool-group .glass-btn');

  // Keyboard listeners
  window.addEventListener('keydown', (e) => {
    // Ignore keystrokes if the user is typing in a text input
    if (e.target instanceof HTMLInputElement) {
      const type = e.target.type.toLowerCase();
      if (['text', 'number', 'password', 'search', 'email', 'tel', 'url'].includes(type)) {
        return;
      }
    }
    if (e.target instanceof HTMLTextAreaElement) return;

    if (e.key.toLowerCase() === 'f') {
      debugPanel?.classList.toggle('hidden');
    } else if (e.key.toLowerCase() === 'h') {
      modalHelp?.classList.toggle('hidden');
    } else if (e.key === '1') {
      UIState.update({ activeTool: 'wedge' });
    } else if (e.key === '2') {
      UIState.update({ activeTool: 'hand' });
    } else if (e.key === 'Escape') {
      UIState.update({ activeTool: null });
    }
  });

  btnHelp?.addEventListener('click', () => {
    modalHelp?.classList.toggle('hidden');
  });

  // Close modal when clicking backdrop
  modalHelp?.addEventListener('click', (e) => {
    if (e.target === modalHelp) {
      modalHelp.classList.add('hidden');
    }
  });

  btnDismissStuck?.addEventListener('click', () => {
    modalStuck?.classList.add('hidden');
  });

  // Bind Tool Buttons
  toolButtons.forEach(btn => {
    btn.addEventListener('click', (e) => {
      const tool = (e.currentTarget as HTMLElement).getAttribute('data-tool') as ToolType;
      UIState.update({ activeTool: tool });
    });
  });

  // Bind Debug Checkboxes (both in debug panel and main panel)
  const checkboxes = document.querySelectorAll('input[type="checkbox"][id^="dbg-"]');
  checkboxes.forEach(cb => {
    const input = cb as HTMLInputElement;
    input.addEventListener('change', () => {
      const id = input.id.replace('dbg-', '');
      if (id === 'showPerfWarnings') {
        UIState.update({ showPerfWarnings: input.checked });
      } else {
        UIState.updateFeature(id as any, input.checked);
      }
    });
  });

  // Bind Sliders
  const gravityInput = document.getElementById('dbg-gravity') as HTMLInputElement;
  if (gravityInput) {
    gravityInput.value = UIState.getState().simulation.gravity.toString();
  }
  gravityInput?.addEventListener('input', () => {
    UIState.updateSimulation('gravity', parseFloat(gravityInput.value));
  });

  const bindingInput = document.getElementById('dbg-binding') as HTMLInputElement;
  if (bindingInput) {
    bindingInput.value = UIState.getState().simulation.bindingThreshold.toString();
  }
  bindingInput?.addEventListener('input', () => {
    UIState.updateSimulation('bindingThreshold', parseFloat(bindingInput.value));
  });

  // Subscribe to state changes to update DOM
  const pressureDisplay = document.getElementById('dbg-pressure-display');

  const tensionGlobalRadio = document.getElementById('tension-global') as HTMLInputElement;
  const tensionLogRadio = document.getElementById('tension-log') as HTMLInputElement;

  tensionGlobalRadio?.addEventListener('change', () => {
    if (tensionGlobalRadio.checked) UIState.updateFeature('globalTension', true);
  });
  tensionLogRadio?.addEventListener('change', () => {
    if (tensionLogRadio.checked) UIState.updateFeature('globalTension', false);
  });

  UIState.subscribe((state) => {
    const tensionModeContainer = document.getElementById('tension-mode-container');
    if (tensionModeContainer) {
      tensionModeContainer.style.display = state.featureFlags.showTension ? 'flex' : 'none';
    }
    if (tensionGlobalRadio) tensionGlobalRadio.checked = state.featureFlags.globalTension;
    if (tensionLogRadio) tensionLogRadio.checked = !state.featureFlags.globalTension;

    // Update Sliders
    if (gravityInput) {
      gravityInput.value = state.simulation.gravity.toString();
    }
    if (bindingInput) {
      bindingInput.value = state.simulation.bindingThreshold.toString();
    }
    if (pressureDisplay) {
      pressureDisplay.textContent = `Current Pressure: ${state.simulation.currentPressure.toFixed(2)}`;
    }

    // Update Mode UI
    if (modePPE) modePPE.checked = state.activeMode === 'PPE';
    if (modeOHLEC) modeOHLEC.checked = state.activeMode === 'OHLEC';
    if (modeSimulator) modeSimulator.checked = state.activeMode === 'SIMULATOR';
    if (modeAssessment) modeAssessment.checked = state.activeMode === 'ASSESSMENT';

    if (state.activeMode === 'PPE') {
      ppePanel?.classList.remove('hidden');
      ohlecPanel?.classList.add('hidden');
      assessmentPanel?.classList.add('hidden');
      modeBackdrop?.classList.add('hidden');
      simulatorControls.forEach(el => el.classList.add('hidden'));

      // Update list items active state and checkbox checks
      PPE_ITEMS.forEach(item => {
        const btn = document.getElementById(`ppe-item-${item.id}`);
        if (btn) {
          if (state.selectedPPEId === item.id) {
            btn.classList.add('selected');
          } else {
            btn.classList.remove('selected');
          }
        }
        const cb = document.getElementById(`ppe-cb-${item.id}`) as HTMLInputElement;
        if (cb) {
          cb.checked = !!state.equippedPPE[item.id];
        }
      });

      // Update detail panel texts & render 3D
      updatePPEDetails(state);
    } else if (state.activeMode === 'OHLEC') {
      ppePanel?.classList.add('hidden');
      if (ppeDetailPanel) ppeDetailPanel.classList.add('hidden');
      if (ppePreview) ppePreview.stop();
      ohlecPanel?.classList.remove('hidden');
      assessmentPanel?.classList.add('hidden');
      modeBackdrop?.classList.remove('hidden');
      simulatorControls.forEach(el => el.classList.add('hidden'));
    } else if (state.activeMode === 'ASSESSMENT') {
      ppePanel?.classList.add('hidden');
      if (ppeDetailPanel) ppeDetailPanel.classList.add('hidden');
      if (ppePreview) ppePreview.stop();
      ohlecPanel?.classList.add('hidden');
      assessmentPanel?.classList.remove('hidden');
      modeBackdrop?.classList.remove('hidden');
      simulatorControls.forEach(el => el.classList.add('hidden'));
    } else { // SIMULATOR
      ppePanel?.classList.add('hidden');
      if (ppeDetailPanel) ppeDetailPanel.classList.add('hidden');
      if (ppePreview) ppePreview.stop();
      ohlecPanel?.classList.add('hidden');
      assessmentPanel?.classList.add('hidden');
      modeBackdrop?.classList.add('hidden');
      simulatorControls.forEach(el => {
        if (el.id !== 'debug-panel') {
          el.classList.remove('hidden');
        }
      });
    }

    // Update Tool Active States
    toolButtons.forEach(btn => {
      if (btn.getAttribute('data-tool') === state.activeTool) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });

    // Update Throttle Fill
    if (throttleFill) {
      if (state.throttleOn) {
        throttleFill.style.width = '100%';
        throttleFill.style.backgroundColor = 'var(--status-green)';
        if (throttleToggle) throttleToggle.checked = true;
      } else {
        throttleFill.style.width = '0%';
        throttleFill.style.backgroundColor = '';
        if (throttleToggle) throttleToggle.checked = false;
      }
    }

    // Update Kerf Width Fill
    const kerfFill = document.getElementById('kerf-fill');
    if (kerfFill) {
      const pressure = state.simulation.currentPressure;
      const maxPressure = state.simulation.bindingThreshold;

      // Calculate kerf width percentage (100% at 0 pressure, 0% at maxPressure)
      const kerfWidthPercent = Math.max(0, Math.min(100, 100 * (1 - pressure / maxPressure)));
      kerfFill.style.width = `${kerfWidthPercent}%`;

      if (pressure <= 50) {
        kerfFill.style.backgroundColor = 'var(--status-green)';
      } else if (pressure < 200) {
        kerfFill.style.backgroundColor = 'var(--status-yellow)';
      } else {
        kerfFill.style.backgroundColor = 'var(--status-red)';
      }
    }
  });

  let wasStuck = false;
  UIState.subscribe((state) => {
    if (state.chainsawStuck && !wasStuck) {
      modalStuck?.classList.remove('hidden');
    } else if (!state.chainsawStuck && wasStuck) {
      modalStuck?.classList.add('hidden');
    }
    wasStuck = state.chainsawStuck;
  });

  // Purchasing Preferences UI Logic
  const modalPrefs = document.getElementById('modal-purchasing-prefs');
  const btnPrefs = document.getElementById('btn-purchasing-prefs');
  const btnSavePrefs = document.getElementById('btn-save-prefs');
  const btnCancelPrefs = document.getElementById('btn-cancel-prefs');
  const defaultStoresList = document.getElementById('default-stores-list');
  const customStoresList = document.getElementById('custom-stores-list');
  const btnAddCustom = document.getElementById('btn-add-custom-store');
  const inputCustom = document.getElementById('input-custom-store') as HTMLInputElement;

  let tempPrefs: StorePrefs | null = null;

  function renderPrefsModal() {
    if (!defaultStoresList || !customStoresList || !tempPrefs) return;
    defaultStoresList.innerHTML = '';
    customStoresList.innerHTML = '';

    // Render defaults
    DEFAULT_STORES_LIST.forEach(store => {
      const label = document.createElement('label');
      label.style.display = 'flex';
      label.style.alignItems = 'center';
      label.style.gap = '0.5rem';
      label.style.cursor = 'pointer';
      
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = !!tempPrefs!.defaults[store.id];
      cb.addEventListener('change', () => {
        tempPrefs!.defaults[store.id] = cb.checked;
      });

      label.appendChild(cb);
      label.appendChild(document.createTextNode(store.name));
      defaultStoresList.appendChild(label);
    });

    // Render customs
    tempPrefs.customs.forEach((domain, idx) => {
      const row = document.createElement('div');
      row.style.display = 'flex';
      row.style.justifyContent = 'space-between';
      row.style.alignItems = 'center';

      const span = document.createElement('span');
      span.textContent = domain;
      
      const rmBtn = document.createElement('button');
      rmBtn.textContent = '🗑️';
      rmBtn.className = 'glass-btn';
      rmBtn.style.padding = '2px 6px';
      rmBtn.style.fontSize = '0.8rem';
      rmBtn.title = 'Remove store';
      rmBtn.addEventListener('click', () => {
        tempPrefs!.customs.splice(idx, 1);
        renderPrefsModal(); // Re-render
      });

      row.appendChild(span);
      row.appendChild(rmBtn);
      customStoresList.appendChild(row);
    });
  }

  btnPrefs?.addEventListener('click', () => {
    tempPrefs = getPurchasingConfig();
    renderPrefsModal();
    modalPrefs?.classList.remove('hidden');
  });

  btnAddCustom?.addEventListener('click', () => {
    if (inputCustom && inputCustom.value.trim() !== '') {
      let val = inputCustom.value.trim().toLowerCase();
      // clean up basic http/https if they pasted it
      val = val.replace(/^https?:\/\//, '').split('/')[0];
      if (tempPrefs && !tempPrefs.customs.includes(val)) {
        tempPrefs.customs.push(val);
        inputCustom.value = '';
        renderPrefsModal();
      }
    }
  });

  btnCancelPrefs?.addEventListener('click', () => {
    modalPrefs?.classList.add('hidden');
  });

  btnSavePrefs?.addEventListener('click', () => {
    if (tempPrefs) {
      savePurchasingConfig(tempPrefs);
    }
    modalPrefs?.classList.add('hidden');
  });

  // Hijack PPE detail buy button click
  const ppeDetailBuy = document.getElementById('ppe-detail-buy');
  ppeDetailBuy?.addEventListener('click', (e) => {
    e.preventDefault();
    const prefs = getPurchasingConfig();
    const activeDomains: string[] = [];
    
    DEFAULT_STORES_LIST.forEach(s => {
      if (prefs.defaults[s.id]) activeDomains.push(s.domain);
    });
    activeDomains.push(...prefs.customs);

    if (activeDomains.length === 0) {
      // No stores configured, open settings modal
      tempPrefs = getPurchasingConfig();
      renderPrefsModal();
      modalPrefs?.classList.remove('hidden');
      return;
    }

    const state = UIState.getState();
    const selectedItem = PPE_ITEMS.find(i => i.id === state.selectedPPEId);
    if (selectedItem) {
      // Construct Google query
      let query = `purchase ${selectedItem.name}`;
      if (activeDomains.length > 0) {
        const sites = activeDomains.map(d => `site:${d}`).join(' OR ');
        query += ` (${sites})`;
      }
      const url = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  });
}
